// Минимальная ширина стрелки "левого" или "правого" типов
// Если доступная ширина меньше, переходим на "средний"
import type { ArrowStyleType, Offset } from '@octopus-do/octopus-project-core'

const NODE_GAP = 50
// Расстояние, на котором "средняя" стрелка проходит справа
const MIDDLE_ARROW_MARGIN = 30

export enum ArrowType {
  REGULAR = 'REGULAR',
  MIDDLE = 'MIDDLE',
}

export enum ArrowOrientation {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export const getArrowClassification = (
  startNode: Element,
  endNode: Element,
  scale: number
): { arrowType: ArrowType; arrowOrientation: ArrowOrientation } => {
  const startRect = startNode.getBoundingClientRect()
  const endRect = endNode.getBoundingClientRect()

  if (startRect.right + NODE_GAP * scale < endRect.left) {
    return {
      arrowType: ArrowType.REGULAR,
      arrowOrientation: ArrowOrientation.RIGHT,
    }
  }

  if (startRect.left - NODE_GAP * scale > endRect.right) {
    return {
      arrowType: ArrowType.REGULAR,
      arrowOrientation: ArrowOrientation.LEFT,
    }
  }

  if (startRect.left > endRect.left) {
    return {
      arrowType: ArrowType.MIDDLE,
      arrowOrientation: ArrowOrientation.LEFT,
    }
  }

  return {
    arrowType: ArrowType.MIDDLE,
    arrowOrientation: ArrowOrientation.RIGHT,
  }
}

export const getMiddlePoint = (
  nodeStart: HTMLElement,
  nodeEnd: HTMLElement,
  arrowOrientation: ArrowOrientation,
  scale: number
): {
  left: number
  top: number
} => {
  const nodeStartRect = nodeStart.getBoundingClientRect()
  const nodeEndRect = nodeEnd.getBoundingClientRect()

  const left =
    arrowOrientation === ArrowOrientation.RIGHT
      ? Math.max(nodeStartRect.right, nodeEndRect.right) +
        MIDDLE_ARROW_MARGIN * scale
      : Math.min(nodeStartRect.left, nodeEndRect.left) -
        MIDDLE_ARROW_MARGIN * scale

  return {
    left,
    top:
      (nodeStartRect.top +
        nodeStartRect.bottom +
        nodeEndRect.top +
        nodeEndRect.bottom) /
      4,
  }
}

export const getBaseArrowPoints = (
  type: ArrowStyleType,
  startId: string,
  endId: string,
  scale: number
): Offset[] => {
  if (type !== 'straight') {
    return []
  }

  const nodeStart = document.getElementById(startId)
  const nodeEnd = document.getElementById(endId)

  if (!nodeStart || !nodeEnd) {
    return []
  }

  const { arrowType, arrowOrientation } = getArrowClassification(
    nodeStart,
    nodeEnd,
    scale
  )

  const { left: x0, top: y0 } = document
    .getElementById('tree')!
    .getBoundingClientRect()

  const y1 =
    nodeStart.getBoundingClientRect().top / 2 +
    nodeStart.getBoundingClientRect().bottom / 2
  const y2 =
    nodeEnd.getBoundingClientRect().top / 2 +
    nodeEnd.getBoundingClientRect().bottom / 2

  let x1: number

  if (arrowType === ArrowType.MIDDLE) {
    x1 = getMiddlePoint(nodeStart, nodeEnd, arrowOrientation, scale).left
  } else {
    x1 =
      arrowOrientation === ArrowOrientation.RIGHT
        ? nodeStart.getBoundingClientRect().right / 2 +
          nodeEnd.getBoundingClientRect().left / 2
        : nodeStart.getBoundingClientRect().left / 2 +
          nodeEnd.getBoundingClientRect().right / 2
  }

  return [y1, y2].map((y) => ({
    x: (x1 - x0) / scale,
    y: (y - y0) / scale,
  }))
}
